/**
 * resolveLayout - Runtime layout selection controller
 *
 * Determines which layout component to render based on PageData.
 * Acts as the controller that orchestrates layout selection.
 *
 * Layout presets:
 * - classic: Two-column layout with header image, tabs, sidebar CTA
 * - hero: Single-column hero banner layout, no sidebar
 */
import type { LayoutPreset } from "@shared/config/layout.config";
import { DEFAULT_LAYOUT_PRESET } from "@shared/config/layout.config";
import { ClassicLayout } from "../presets/classic/ClassicLayout.web";
import { HeroLayout } from "../presets/hero/HeroLayout.web";
import type { PageData, LayoutComponent } from "../adapters/types";

// Re-export LayoutPreset type for convenience
export type { LayoutPreset };

/**
 * Determines the layout preset based on PageData
 *
 * Priority:
 * 1. Explicit layoutPreset in PageData (from adapter/branding config)
 * 2. Default to "classic" layout
 *
 * Note: "attraction" is a VARIANT of layouts, not a separate preset.
 * Both ClassicLayout and HeroLayout can handle attraction pages internally.
 */
export function resolveLayoutPreset(data: PageData): LayoutPreset {
  // Check for explicit layout preset from PageData
  // This will eventually come from useBrandingConfig hook via the adapter
  if (data.layoutPreset) {
    return data.layoutPreset;
  }

  // Default to classic layout
  return DEFAULT_LAYOUT_PRESET;
}

/**
 * Returns the appropriate layout component based on PageData
 *
 * This is the main controller function that orchestrates layout selection.
 */
export function resolveLayout(data: PageData): LayoutComponent {
  const layoutPreset = resolveLayoutPreset(data);

  switch (layoutPreset) {
    case "hero":
      return HeroLayout;
    case "classic":
    default:
      return ClassicLayout;
  }
}

/**
 * Layout registry for extensibility and testing
 *
 * All available layout presets are registered here.
 */
export const LAYOUT_REGISTRY: Record<LayoutPreset, LayoutComponent> = {
  classic: ClassicLayout,
  hero: HeroLayout,
};
